import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Dialog, IconButton, Skeleton, Typography } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import TaskImageCanvas from '../tasks/view/TaskImageCanvas';

// ── Styles ────────────────────────────────────────────────────────────────────

const controlBg = theme => (theme.palette.mode === 'dark' ? 'rgba(13,17,23,0.88)' : 'rgba(255,255,255,0.82)');

const styles = {
  imageBlock: {
    bgcolor: theme => theme.bruno.colors.inactive, borderRadius: 3, overflow: 'hidden', position: 'relative',
    display: 'flex', alignItems: 'center', justifyContent: 'center', touchAction: 'pan-y',
  },
  imageLoadingContainer: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 4,
  },
  imageFullscreenButton: {
    position: 'absolute', top: 8, right: 8, zIndex: 1,
    bgcolor: controlBg, color: 'text.primary', boxShadow: 1,
    '&:hover': { bgcolor: 'background.paper' },
  },
  imageFullscreenIcon: { fontSize: 14 },
  imageDots: {
    display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: -0.5,
  },
  imageDot: {
    width: 8, height: 8, borderRadius: '50%', opacity: 0.5,
    cursor: 'pointer', transition: 'background 0.2s, opacity 0.2s', flexShrink: 0,
  },
  navigationArrow: { fontSize: 14 },
  fullscreenDialog: { bgcolor: '#111', m: 1, overflow: 'hidden' },
  fullscreenContainer: { position: 'relative', width: '100%', height: '80vh' },
  fullscreenCloseButton: {
    position: 'absolute', top: 8, right: 8,
    color: '#fff', bgcolor: 'rgba(0,0,0,0.4)', zIndex: 1,
    '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
  },
};

// Default url resolver — handles base64, object url, plain string
function defaultGetUrl(img) {
  if (!img) return null;
  if (img?.metadata?.encoded_image) return `data:image/jpeg;base64,${img.metadata.encoded_image}`;
  if (typeof img?.url === 'object') return img.url?.imageUrl ?? null;
  if (typeof img === 'string') return img;
  return img?.url ?? null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ImageCarousel({
  images,
  loading = false,
  isMobile = false,
  imageHeight,
  getUrl = defaultGetUrl,
  renderMetadata,        // optional: (currentImage) => ReactNode
  framing = false,       // auto-crop overhead images (task detail only)
}) {
  const { t } = useTranslation();
  const [activeImg, setActiveImg] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [fsNaturalSize, setFsNaturalSize] = useState(null);
  const [fsZoom, setFsZoom] = useState(1);
  const imgSwipeXRef = useRef(null);
  const didSwipeRef = useRef(false);
  const fsScrollRef = useRef(null);
  const fsImgRef = useRef(null);
  const pendingScrollRef = useRef(null);
  const imgHeight = imageHeight ?? (isMobile ? 200 : 240);

  useEffect(() => { setActiveImg(0); setImgLoaded(false); setImgError(false); }, [images]);
  useEffect(() => { if (!fullscreen) { setFsNaturalSize(null); setFsZoom(1); } }, [fullscreen]);
  useEffect(() => {
    if (!pendingScrollRef.current || !fsScrollRef.current) return;
    const { left, top } = pendingScrollRef.current;
    fsScrollRef.current.scrollLeft = left;
    fsScrollRef.current.scrollTop = top;
    pendingScrollRef.current = null;
  }, [fsZoom]);
  useEffect(() => { setImgLoaded(false); setImgError(false); }, [activeImg]);

  const handleFsClick = useCallback((e) => {
    const container = fsScrollRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    const newZoom = fsZoom === 1 ? 2 : 1;
    const ratio = newZoom / fsZoom;
    pendingScrollRef.current = {
      left: Math.max(0, (container.scrollLeft + clickX) * ratio - clickX),
      top: Math.max(0, (container.scrollTop + clickY) * ratio - clickY),
    };
    setFsZoom(newZoom);
  }, [fsZoom]);

  const hasImages = images && images.length > 0;
  const current = hasImages ? images[activeImg] : null;
  const imgSrc = getUrl(current);

  // Auto-crop only overhead images; everything else renders full.
  const frameMode = framing && current?.type === 'overhead' ? 'auto' : 'full';

  return (
    <>
      {/* ── Image block ── */}
      <Box
        sx={{ ...styles.imageBlock, height: imgHeight, flexShrink: 0 }}
        onPointerDown={(e) => {
          didSwipeRef.current = false;
          if (hasImages && images.length > 1) imgSwipeXRef.current = e.clientX;
        }}
        onPointerUp={(e) => {
          const startX = imgSwipeXRef.current;
          if (startX == null || !hasImages || images.length <= 1) return;
          imgSwipeXRef.current = null;
          const dx = e.clientX - startX;
          if (Math.abs(dx) < 30) return;
          didSwipeRef.current = true;
          setActiveImg(p => dx < 0 ? (p + 1) % images.length : (p - 1 + images.length) % images.length);
        }}
      >
        {loading ? (
          <Box sx={styles.imageLoadingContainer}>
            <Skeleton variant="rounded" width={64} height={64} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" width={100} height={12} sx={{ borderRadius: 1 }} />
          </Box>
        ) : hasImages && imgSrc ? (
          <>
            {!imgLoaded && !imgError && (
              <Box sx={styles.imageLoadingContainer}>
                <Skeleton variant="rounded" width={120} height={120} sx={{ borderRadius: 2 }} />
                <Skeleton variant="rounded" width={140} height={16} sx={{ borderRadius: 1 }} />
              </Box>
            )}
            {imgError && (
              <Box sx={styles.imageLoadingContainer}>
                <Box component="span" className="material-icons" sx={{ fontSize: 36, color: 'text.disabled' }}>broken_image</Box>
                <Typography variant="caption" color="text.disabled">
                  {t('bruno.products.image_load_error', 'Image could not be loaded')}
                </Typography>
              </Box>
            )}
            <Box
              sx={{ display: imgLoaded && !imgError ? 'block' : 'none', cursor: 'zoom-in' }}
              onClick={() => { if (!didSwipeRef.current) setFullscreen(true); }}
            >
              <TaskImageCanvas
                src={imgSrc}
                metadata={current?.metadata}
                type={current?.type}
                width={320}
                height={imgHeight}
                strokeWidth={2}
                highDpi
                frameMode={frameMode}
                onDrawn={() => setImgLoaded(true)}
                onError={() => { setImgLoaded(true); setImgError(true); }}
              />
            </Box>
            {imgLoaded && !imgError && (
              <IconButton size="small" onClick={() => setFullscreen(true)} sx={styles.imageFullscreenButton}>
                <OpenInFullIcon sx={styles.imageFullscreenIcon} />
              </IconButton>
            )}
          </>
        ) : (
            <Box sx={styles.imageLoadingContainer}>
              <Box component="span" className="material-icons" sx={{ fontSize: 36, color: 'text.disabled' }}>image_not_supported</Box>
              <Typography variant="caption" color="text.disabled">
                {t('bruno.tasks_details.no_images', 'No images')}
              </Typography>
          </Box>
        )}
      </Box>

      {/* ── Dots + arrows ── */}
      {!loading && hasImages && images.length > 1 && (
        <Box sx={styles.imageDots}>
          <IconButton size="small" onClick={() => setActiveImg(p => (p - 1 + images.length) % images.length)}>
            <ArrowBackIosNewIcon sx={styles.navigationArrow} />
          </IconButton>
          {images.map((_, i) => (
            <Box
              key={i}
              onClick={() => setActiveImg(i)}
              sx={{ ...styles.imageDot, bgcolor: i === activeImg ? 'text.primary' : 'text.disabled', ...(i === activeImg && { opacity: 1 }) }}
            />
          ))}
          <IconButton size="small" onClick={() => setActiveImg(p => (p + 1) % images.length)}>
            <ArrowForwardIosIcon sx={styles.navigationArrow} />
          </IconButton>
        </Box>
      )}

      {/* ── Optional metadata block below image ── */}
      {!loading && renderMetadata?.(current)}

      {/* ── Fullscreen dialog ── */}
      <Dialog
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        maxWidth="md"
        fullWidth
        style={{ zIndex: 1400 }}
        PaperProps={{ sx: styles.fullscreenDialog }}
      >
        <Box ref={fsScrollRef} sx={{ position: 'relative', width: '100%', height: '85vh', overflow: 'auto' }}>
          <IconButton onClick={() => setFullscreen(false)} size="small" sx={{ ...styles.fullscreenCloseButton, position: 'fixed' }}>
            <CloseIcon />
          </IconButton>
          <Box
            sx={{ position: 'relative', width: `${fsZoom * 100}%`, cursor: fsZoom === 1 ? 'zoom-in' : 'zoom-out' }}
            onClick={handleFsClick}
          >
            <Box
              ref={fsImgRef}
              component="img"
              src={imgSrc}
              alt=""
              sx={{ display: 'block', width: '100%', height: 'auto', pointerEvents: 'none' }}
              onLoad={(e) => {
                const { naturalWidth: nw, naturalHeight: nh } = e.target;
                setFsNaturalSize({ nw, nh });
                const meta = current?.metadata;
                if (meta?.y1 != null && meta?.y2 != null && fsScrollRef.current) {
                  const bboxCenterFraction = (meta.y1 + meta.y2) / 2 / nh;
                  const imgH = e.target.offsetHeight;
                  const containerH = fsScrollRef.current.offsetHeight;
                  const scrollTarget = bboxCenterFraction * imgH - containerH / 2;
                  fsScrollRef.current.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'smooth' });
                }
              }}
            />
            {fsNaturalSize && current?.metadata?.x1 != null && (
              <Box sx={{
                position: 'absolute',
                left: `${(current.metadata.x1 / fsNaturalSize.nw) * 100}%`,
                top: `${(current.metadata.y1 / fsNaturalSize.nh) * 100}%`,
                width: `${((current.metadata.x2 - current.metadata.x1) / fsNaturalSize.nw) * 100}%`,
                height: `${((current.metadata.y2 - current.metadata.y1) / fsNaturalSize.nh) * 100}%`,
                border: `3px solid ${current?.type === 'overhead' ? '#ffa400' : '#e91e63'}`,
                pointerEvents: 'none',
              }} />
            )}
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
